interface UsageStep {
  step: string;
  label: string;
  desc: string;
}

interface Product {
  id: number;
  name: string;
  category: string;
  desc: string;
  image: string;
  purchaseUrl?: string;
  detail: {
    subtitle: string;
    highlights: string[];
    description: string;
    usage: string;
    caution: string;
    detailImage?: string;
    usageSteps?: UsageStep[];
    cautionList?: string[];
  };
}

interface Props {
  product: Product;
}

const ProductJsonLd = ({ product }: Props) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.detail.description,
    image: [product.image, product.detail.detailImage].filter(Boolean),
    category: product.category,
    brand: {
      '@type': 'Brand',
      name: '전립상회 PAC KOREA',
    },
    url: `${siteUrl}/product/${product.id}`,
    additionalProperty: product.detail.highlights.map((h, i) => ({
      '@type': 'PropertyValue',
      name: `특징 ${i + 1}`,
      value: h,
    })),
    offers: {
      '@type': 'Offer',
      url: product.purchaseUrl || 'https://smartstore.naver.com/proactivecare',
      priceCurrency: 'KRW',
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        name: '전립상회 PAC KOREA',
        url: siteUrl,
      },
    },
    manufacturer: {
      '@type': 'Organization',
      name: '전립상회 PAC KOREA',
      url: siteUrl,
    },
    isRelatedTo: {
      '@type': 'Thing',
      name: '건강기능식품',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '홈',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '제품소개',
        item: `${siteUrl}/#products`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: product.name,
        item: `${siteUrl}/product/${product.id}`,
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${product.name} | 전립상회 PAC KOREA 건강기능식품`,
    url: `${siteUrl}/product/${product.id}`,
    description: `${product.detail.description.slice(0, 120)}... 전립상회 PAC KOREA 공식 제품. 식약처 인증·GMP 공장 생산.`,
    inLanguage: 'ko-KR',
    isPartOf: {
      '@type': 'WebSite',
      name: '전립상회 PAC KOREA',
      url: siteUrl,
    },
    breadcrumb: breadcrumbSchema,
  };

  const faqItems: object[] = [];

  if (product.detail.usage) {
    faqItems.push({
      '@type': 'Question',
      name: `${product.name}은(는) 어떻게 섭취하나요?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: product.detail.usage,
      },
    });
  }

  if (product.detail.cautionList && product.detail.cautionList.length > 0) {
    faqItems.push({
      '@type': 'Question',
      name: `${product.name} 섭취 시 주의사항은 무엇인가요?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: product.detail.cautionList.join(' '),
      },
    });
  }

  faqItems.push({
    '@type': 'Question',
    name: `${product.name}은(는) 어디서 구매할 수 있나요?`,
    acceptedAnswer: {
      '@type': 'Answer',
      text: `${product.name}은(는) 네이버 스마트스토어(https://smartstore.naver.com/proactivecare)에서 구매하실 수 있습니다. 네이버페이 포인트 적립 및 다양한 할인 혜택을 제공합니다.`,
    },
  });

  faqItems.push({
    '@type': 'Question',
    name: `${product.name}은(는) 식약처 인증 제품인가요?`,
    acceptedAnswer: {
      '@type': 'Answer',
      text: `네, ${product.name}은(는) 식약처 인증 건강기능식품이며 HACCP·GMP 인증 공장에서 생산됩니다.`,
    },
  });

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
};

export default ProductJsonLd;
